/**
 * Crew documents & certifications.
 *
 * Backed by `/api/crews/document-certifications/`, the same endpoint the crew
 * mobile app writes to — `type_name` is free text, so the category titles below
 * must stay byte-for-byte identical to the mobile app's or the two clients will
 * disagree about what has been uploaded.
 */

export interface CrewDocumentRecord {
  id: number;
  type_name: string;
  crew_member: number;
  document: string;
}

/** The file is appended to the FormData separately. */
export type CrewDocumentPayload = Pick<
  CrewDocumentRecord,
  "type_name" | "crew_member"
>;

export interface CrewDocumentInput {
  typeName: string;
  /** Omitted on a metadata-only update (rename the category, keep the file). */
  file: File | null;
}

export interface CrewDocumentCategory {
  type_name: string;
  description: string;
  required: boolean;
}

/** Mirrors the hard-coded category list in the crew mobile app. */
export const CREW_DOCUMENT_CATEGORIES: CrewDocumentCategory[] = [
  {
    type_name: "ID Document",
    description: "Passport, Driver's License, or National ID",
    required: true,
  },
  {
    type_name: "Certificates",
    description: "Academic and professional certificates",
    required: true,
  },
  {
    type_name: "Proof of Address",
    description: "Utility bill or bank statement",
    required: true,
  },
  {
    type_name: "Bank Details",
    description: "Bank account information",
    required: true,
  },
  {
    type_name: "Medical Aid",
    description: "Medical insurance documents",
    required: false,
  },
];

export const UNKNOWN_CATEGORY_DESCRIPTION = "Uploaded document";

export { extractCollection } from "./collection";
export type { Collection } from "./collection";

/**
 * `document` comes back as a full, often signed URL — the display name is the
 * last path segment once the query string is stripped.
 */
export function getDocumentName(uri: string): string {
  if (!uri) return "Document";
  const clean = uri.split("?")[0];
  const segments = clean.split("/");
  const name = segments[segments.length - 1];
  if (!name) return "Document";
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
}

export function getDocumentExtension(uri: string): string {
  const name = getDocumentName(uri);
  const dot = name.lastIndexOf(".");
  return dot > -1 ? name.slice(dot + 1).toUpperCase() : "FILE";
}
